import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { Input } from "@/components/ui/input.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { useEffect, useState } from "react";

type NumberInputComponentData = Extract<ArtifactComponent, { type: "numberInput" }>;

export const NumberInputBlock = ({ component }: { component: NumberInputComponentData }) => {
  const { isReadOnly, patchOne } = useArtifactViewCtx();
  const canEdit = !isReadOnly && component.editable;

  const [draft, setDraft] = useState(component.value?.toString() ?? "");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) setDraft(component.value?.toString() ?? "");
  }, [component.value, dirty]);

  const save = async () => {
    // A required field can't be left blank — clearing it writes `min ?? 0`
    // (mirrors the legacy `v ?? 0` coercion) instead of `null`, which the
    // server applier would otherwise ignore, leaving the field looking dead.
    const emptyValue = component.required ? (component.min ?? 0) : null;
    const parsed = draft === "" ? emptyValue : Number(draft);
    if (parsed !== null && Number.isNaN(parsed)) return;
    if (parsed === component.value) return;
    try {
      await patchOne(component.editPath, parsed);
    } catch (error) {
      console.error(error);
      errorToast("Error updating field");
    }
  };

  return (
    <div className="flex items-center gap-2 mt-1.5">
      {component.label ? (
        <span className="text-xs text-muted-foreground w-28 shrink-0">{component.label}</span>
      ) : null}
      <Input
        type="number"
        value={draft}
        min={component.min}
        max={component.max}
        step={component.step}
        placeholder={component.placeholder}
        readOnly={!canEdit}
        onChange={(e) => {
          setDraft(e.target.value);
          setDirty(true);
        }}
        onBlur={async () => {
          if (dirty) {
            await save();
          }
          setDirty(false);
        }}
        className="h-7 text-xs w-24"
      />
      {component.suffix ? (
        <span className="text-xs text-muted-foreground">{component.suffix}</span>
      ) : null}
    </div>
  );
};
